import { useEffect, useRef, useState } from "react";
import Icon from "../Icon";
import DatePicker from "../forms/DatePicker";
import FechasDeControl from "../widgets/FechasDeControl";
import { Avatar, Badge } from "../Atoms";
import { PJ_ESTADOS, PJ_PRIOS, PJ_NODOS, PJ_ROLES, ST_CLR, PR_CLR, PR_BG } from "../../constants";
import { fmtHours, puedeEditarCampoTarea, puedeEditarTarea } from "../../utils";
import { salidasDe, claveNueva, etiquetaPropia } from "./flowGeometry";
import Campo from "../forms/Campo";
import { clicable } from "../ClicableTeclado.jsx";

// Panel de propiedades. Calcado del DetailPanel de NotionProjectsPage, con tres
// diferencias deliberadas:
//
//  1. Usa la clase .panel de constants.js en vez de inyectar sus propias
//     @keyframes, que es la deuda que dejo aquel fichero.
//  2. SIN velo en escritorio: el velo bloquea el lienzo, y aqui se quiere seguir
//     viendo y moviendo el diagrama con el panel abierto.
//  3. Autoguarda. No hay pie de "Descartar / Guardar cambios": mezclarlo con el
//     autoguardado del lienzo seria incoherente.
//
// Texto con debounce, selects inmediatos: la misma regla que updateEntry en
// CuadernoPage.

const DEBOUNCE_TEXTO = 600;

// Lo que el panel TAPA del lienzo. Se exporta porque el lienzo necesita el dato:
// el panel flota encima del SVG y no lo encoge, asi que sin decirselo, encajar
// la vista o centrar un nodo lo colocaria debajo del panel creyendo que ese
// espacio esta libre.
export const ANCHO_PANEL = 336;

export default function StepPanel({
  nodo, arista, nodos, proyecto, currentUser, teams, conexiones, minutos, isMobile,
  prevision, festivos = {},
  onCambiarPaso, onBorrarPaso, onCambiarArista, onBorrarArista, onSeleccionar, onCerrar,
  crono, agendaTiposData = [], misPasos = [],
  // La confirmacion la pinta el contenedor del flujo, no el panel: es una
  // sola en toda la vista y tiene que poder tapar el lienzo, no esta columna
  // de 336 px. Aqui solo se pide y se espera la respuesta.
  pedirConfirmacion,
}) {
  const pendientes = useRef({});
  const timers = useRef({});

  // Los timers pendientes se vacian al cambiar de elemento o al desmontar, o el
  // texto a medio escribir se perderia.
  const flush = () => {
    for (const [id, patch] of Object.entries(pendientes.current)) {
      clearTimeout(timers.current[id]);
      if (patch && Object.keys(patch).length) onCambiarPaso?.(id, patch);
    }
    pendientes.current = {};
  };
  useEffect(() => flush, []); // eslint-disable-line

  const cambiarTexto = (id, patch) => {
    pendientes.current[id] = { ...(pendientes.current[id] || {}), ...patch };
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => {
      const p = pendientes.current[id];
      delete pendientes.current[id];
      if (p) onCambiarPaso?.(id, p);
    }, DEBOUNCE_TEXTO);
  };

  const cambiarYa = (id, patch) => {
    // Vaciar lo pendiente antes, para que no llegue despues y pise este cambio
    if (pendientes.current[id]) {
      clearTimeout(timers.current[id]);
      const p = pendientes.current[id];
      delete pendientes.current[id];
      onCambiarPaso?.(id, { ...p, ...patch });
      return;
    }
    onCambiarPaso?.(id, patch);
  };

  // A la IZQUIERDA, no a la derecha.
  //
  // El flujo se lee hacia la derecha, asi que el lado derecho es justo donde se
  // esta trabajando: el panel tapaba lo que venia despues del paso que estabas
  // mirando. A la izquierda solo cubre lo que ya has recorrido —como mucho el
  // nodo de inicio— y el diagrama sigue creciendo a la vista.
  const marco = {
    position: "absolute", top: 0, left: 0, bottom: 0,
    width: isMobile ? "100%" : ANCHO_PANEL, zIndex: 5,
    background: "var(--surface)", borderRight: "1px solid var(--border)",
    boxShadow: "var(--shadow-lg)", overflowY: "auto", padding: 15,
  };

  if (!nodo && !arista) return null;

  // ── Arista seleccionada ────────────────────────────────────────────────────
  if (arista) {
    const de = nodos[String(arista.from_task_id)];
    const a  = nodos[String(arista.to_task_id)];
    const editable = !!proyecto?.can_edit;
    return (
      <div className="panel df-panel" style={marco}>
        <Cabecera titulo="Conexion" onCerrar={onCerrar} />
        <div style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6, marginBottom: 14 }}>
          <b>{de?.title || "?"}</b>
          <span style={{ color: "var(--text3)" }}> → </span>
          <b>{a?.title || "?"}</b>
        </div>

        <div className="cw-field" style={{ marginBottom: 11 }}>
          <Campo><label>Etiqueta de la rama</label>
          {/* El campo tiene que decir lo MISMO que el lienzo. Con
              arista.etiqueta a secas mostraba "Si" en una flecha que no rotula
              nada, y quien lo viera no entenderia por que no sale. */}
          <input defaultValue={etiquetaPropia(arista) || ""} disabled={!editable}
            placeholder={arista.from_handle === "si" || arista.from_handle === "no"
              ? "la rama ya se ve por el color" : "sin etiqueta"}
            onBlur={e => e.target.value !== (arista.etiqueta || "")
              && onCambiarArista?.(arista.id, { etiqueta: e.target.value || null })} /></Campo>
        </div>

        <div className="cw-field" style={{ marginBottom: 11 }}>
          <Campo><label>Condicion (para quien lea el diagrama)</label>
          <input defaultValue={arista.condicion || ""} disabled={!editable}
            placeholder="Si el importe pasa de 5.000 €"
            onBlur={e => e.target.value !== (arista.condicion || "")
              && onCambiarArista?.(arista.id, { condicion: e.target.value || null })} /></Campo>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12,
          color: "var(--text2)", cursor: editable ? "pointer" : "default", marginBottom: 16 }}>
          <input type="checkbox" checked={!!arista.opcional} disabled={!editable}
            onChange={e => onCambiarArista?.(arista.id, { opcional: e.target.checked })} />
          Rama opcional
          <span style={{ fontSize: 10.5, color: "var(--text3)" }}>(se pinta punteada)</span>
        </label>

        {editable && (
          <button className="btn" onClick={() => onBorrarArista?.(arista.id)}
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              fontSize: 12, fontFamily: "var(--font)", background: "var(--red-bg)",
              border: "1px solid #fca5a5", color: "var(--red-text)", fontWeight: 600 }}>
            Eliminar la conexion
          </button>
        )}
      </div>
    );
  }

  // ── Paso seleccionado ──────────────────────────────────────────────────────
  const t = nodo;
  const id = String(t.id);
  const kind = t.kind || "tarea";
  const esTrabajo = kind === "tarea";
  // Inicio y Fin no se planifican: sus fechas son las del proyecto. El mismo
  // criterio que en la lista de pasos, con el mismo componente.
  const esDelimitador = kind === "inicio" || kind === "fin";
  const editableTarea = puedeEditarTarea(currentUser, t, proyecto, teams);
  const puede = campo => puedeEditarCampoTarea(currentUser, t, proyecto, campo, teams);
  const nodoMeta = PJ_NODOS.find(n => n.key === kind) || PJ_NODOS[0];
  const cx = conexiones?.[id] || { salen: [], llegan: [] };

  const est = Number(t.estimacion_min) || 0;
  const real = Number(minutos) || 0;
  const salidas = salidasDe(t);
  // Cambiar las salidas es reorganizar el flujo, no avanzar el propio trabajo:
  // va con el permiso del proyecto, igual que la condicion de un rombo.
  const puedeSalidas = puede("salidas");

  return (
    <div className="panel df-panel" style={marco}>
      <Cabecera titulo={nodoMeta.label} icono={nodoMeta.icon}
        color={esTrabajo ? ST_CLR[t.status] : "var(--text3)"} onCerrar={onCerrar} />

      <div className="cw-field" style={{ marginBottom: 11 }}>
        <Campo><label>Titulo</label>
        <input defaultValue={t.title} disabled={!puede("title")} key={id + "-title"}
          onChange={e => cambiarTexto(id, { title: e.target.value })} /></Campo>
      </div>

      <div className="cw-field" style={{ marginBottom: 11 }}>
        <Campo><label>Descripcion</label>
        <textarea rows={3} defaultValue={t.description || ""} key={id + "-desc"}
          disabled={!puede("description")}
          onChange={e => cambiarTexto(id, { description: e.target.value })}
          placeholder="Que hay que hacer, y como se sabe que esta hecho" /></Campo>
      </div>

      {/* Empezar a cronometrar este paso, sin salir del flujo. Solo para
          trabajo: un nodo de control no es trabajo de nadie.
          El paso bloqueado se ve y se explica, no se esconde: es el mismo
          criterio que el buscador del cronometro. */}
      {esTrabajo && crono && crono.disponible !== false && (() => {
        const activo = crono.activo && String(crono.activo.task_id) === String(id);
        const enLista = misPasos.find(p => String(p.id) === String(id));
        const bloqueado = !!enLista?.bloqueado;
        const espera = (enLista?.bloqueada_por || []).map(x => `«${x.title}»`).join(" y ");
        // El tipo se resuelve por su origen, no por su nombre: cada uno llama a
        // los suyos como quiere.
        const tipo = agendaTiposData.find(x => !x.archived && x.origen === "paso");
        return (
          <div style={{ marginBottom: 11 }}>
            <button className="btn" disabled={crono.guardando || (bloqueado && !activo)}
              onClick={async () => {
                if (activo) { await crono.pausar(crono.activo.id); return; }
                if (!tipo) return;
                await crono.iniciar({ type: tipo.name, description: t.title, task_id: id });
              }}
              title={bloqueado ? `Espera a ${espera}` : (tipo ? "" : "Configura un tipo con origen «Paso de proyecto»")}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
                gap: 6, padding: "8px 12px", borderRadius: 9, fontFamily: "var(--font)",
                fontSize: 12, fontWeight: 700,
                cursor: bloqueado && !activo ? "not-allowed" : "pointer",
                opacity: bloqueado && !activo ? .55 : 1,
                border: "1px solid " + (activo ? "#f59e0b" : "var(--border)"),
                background: activo ? "#fffbeb" : "var(--surface2)",
                color: activo ? "#b45309" : "var(--text2)" }}>
              <Icon name={activo ? "pause-circle-fill" : (bloqueado ? "sign-stop-fill" : "play-fill")}
                size={13} color={activo ? "#b45309" : (bloqueado ? "#a16207" : "var(--ok-text)")} />
              {activo ? "Pausar el cronometro" : bloqueado ? "Bloqueado" : "Empezar a cronometrar"}
            </button>
            {bloqueado && !activo && espera && (
              <div style={{ fontSize: 10.5, color: "var(--warn-text)", marginTop: 5, lineHeight: 1.4 }}>
                Espera a {espera}. Se desbloquea en cuanto se marque como terminada.
              </div>
            )}
            {!bloqueado && !tipo && (
              <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 5, lineHeight: 1.4 }}>
                Ningun tipo de tu cuaderno tiene origen «Paso de proyecto».
              </div>
            )}
          </div>
        );
      })()}

      {esTrabajo && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 11 }}>
          <div className="cw-field">
            <Campo><label>Estado</label>
            <select value={t.status} disabled={!puede("status")}
              onChange={e => cambiarYa(id, { status: e.target.value })}>
              {PJ_ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
            </select></Campo>
          </div>
          <div className="cw-field">
            <Campo><label>Prioridad</label>
            <select value={t.priority} disabled={!puede("priority")}
              onChange={e => cambiarYa(id, { priority: e.target.value })}>
              {PJ_PRIOS.map(p => <option key={p} value={p}>{p}</option>)}
            </select></Campo>
          </div>
        </div>
      )}

      {/* Las fechas van con el DatePicker de la casa, no con el <input
          type="date"> del navegador: el nativo pinta "dd/mm/aaaa" y un
          calendario distinto en cada navegador y sistema, en su idioma y con su
          semana empezando en domingo, dentro de un panel que por lo demas es
          todo de la aplicacion.
          El "Fin" arranca en el mes del "Inicio" (refDate) y no acepta una
          fecha anterior a el (min): un paso que acaba antes de empezar no es un
          aviso que haya que dar, es un dato que no hay que poder meter. */}
      {esDelimitador ? (
        <div style={{ marginBottom: 11 }}>
          <FechasDeControl kind={kind} proyecto={proyecto}
            prevision={prevision} festivos={festivos} />
        </div>
      ) : (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 11 }}>
        <div className="cw-field">
          <Campo><label>Inicio</label>
          <DatePicker value={t.start_date || ""} nombre="Fecha de inicio"
            placeholder="Sin fecha" disabled={!puede("start_date")} margenInferior={0}
            max={t.due_date || ""}
            onChange={v => cambiarYa(id, { start_date: v || null })} /></Campo>
        </div>
        <div className="cw-field">
          <Campo><label>Fin</label>
          <DatePicker value={t.due_date || ""} nombre="Fecha de fin"
            placeholder="Sin fecha" disabled={!puede("due_date")} margenInferior={0}
            refDate={t.start_date || ""} min={t.start_date || ""}
            onChange={v => cambiarYa(id, { due_date: v || null })} /></Campo>
        </div>
      </div>
      )}

      {esTrabajo && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 11 }}>
          <div className="cw-field">
            <Campo><label>Tipo</label>
            <input defaultValue={t.tipo || ""} key={id + "-tipo"} disabled={!puede("tipo")}
              onChange={e => cambiarTexto(id, { tipo: e.target.value })}
              placeholder="Desarrollo…" /></Campo>
          </div>
          <div className="cw-field">
            <Campo><label>Estimacion (h)</label>
            <input type="number" min={0} step={0.5} key={id + "-est"}
              defaultValue={est ? est / 60 : ""} disabled={!puede("estimacion_min")}
              onBlur={e => {
                const h = parseFloat(e.target.value);
                const min = isNaN(h) ? null : Math.round(h * 60);
                if (min !== (est || null)) cambiarYa(id, { estimacion_min: min });
              }} placeholder="sin estimar" /></Campo>
          </div>
        </div>
      )}

      {/* Salidas de un switch. Viven en el nodo, no en las aristas: renombrar
          una salida actualiza todas sus flechas de golpe. Y las claves no se
          reutilizan, asi que borrar o reordenar no remapea las conexiones
          existentes a la salida equivocada. */}
      {kind === "switch" && (
        <Bloque titulo={`Salidas (${salidas.length})`}>
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginBottom: 7,
            lineHeight: 1.5 }}>
            Cada salida lleva a un solo paso. Al renombrarla se actualizan sus
            flechas.
          </div>
          {salidas.map((sal, i) => {
            const esResto = sal.clave === "resto";
            return (
              <div key={sal.clave} style={{ display: "flex", alignItems: "center",
                gap: 5, marginBottom: 4 }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 9.5,
                  color: "var(--text3)", width: 34, flexShrink: 0 }}>
                  {esResto ? "resto" : i + 1}
                </span>
                <input aria-label="Nombre de la salida" defaultValue={sal.nombre} disabled={!puedeSalidas}
                  key={sal.clave + "-" + sal.nombre}
                  placeholder={esResto ? "Resto" : "Nombre de la salida"}
                  onBlur={e => {
                    const nombre = e.target.value.trim();
                    if (!nombre || nombre === sal.nombre) return;
                    onCambiarPaso?.(id, {
                      salidas: salidas.map(x =>
                        x.clave === sal.clave ? { ...x, nombre } : x),
                    });
                  }}
                  style={{ flex: 1, padding: "4px 7px", borderRadius: 6, fontSize: 11.5,
                    border: "1px solid var(--border)", fontFamily: "var(--font)",
                    background: "var(--surface2)", color: "var(--text)",
                    fontStyle: esResto ? "italic" : "normal" }} />
                {puedeSalidas && salidas.length > 1 && (
                  <button aria-label="Quitar esta salida" className="btn" title="Quitar esta salida"
                    onClick={async () => {
                      // Se avisa de las flechas que se van con ella: borrar una
                      // salida deja sus conexiones sin puerto, y callarlo seria
                      // perder aristas en silencio.
                      const cuantas = (cx.salen || [])
                        .filter(e => e.from_handle === sal.clave).length;
                      const ok = await pedirConfirmacion({
                        titulo: `¿Quitar la salida «${sal.nombre}»?`,
                        mensaje: cuantas
                          ? `Se llevara ${cuantas} conexion${cuantas > 1 ? "es" : ""}: `
                            + "las flechas que salian por ella se quedarian sin puerto."
                          : "No tiene ninguna conexion colgando.",
                        etiquetaOk: "Quitar", peligro: true,
                      });
                      if (!ok) return;
                      for (const e of (cx.salen || []).filter(e => e.from_handle === sal.clave))
                        onBorrarArista?.(e.id);
                      onCambiarPaso?.(id, {
                        salidas: salidas.filter(x => x.clave !== sal.clave),
                      });
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer",
                      padding: 3 }}>
                    <Icon name="x-lg" size={10} color="var(--text3)" />
                  </button>
                )}
              </div>
            );
          })}

          {puedeSalidas && (
            <div style={{ display: "flex", gap: 6, marginTop: 7 }}>
              <button className="btn"
                onClick={() => onCambiarPaso?.(id, {
                  salidas: [
                    ...salidas.filter(x => x.clave !== "resto"),
                    { clave: claveNueva(salidas), nombre: `Opcion ${salidas.filter(x => x.clave !== "resto").length + 1}` },
                    ...salidas.filter(x => x.clave === "resto"),
                  ],
                })}
                style={{ padding: "4px 9px", borderRadius: 6, fontSize: 11,
                  cursor: "pointer", border: "1px solid var(--border)",
                  background: "var(--surface)", color: "var(--text2)",
                  fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 4 }}>
                <Icon name="plus-lg" size={9} color="var(--text3)" />Añadir salida
              </button>

              {/* La salida "resto" recoge lo que no encaja en ninguna otra.
                  Evita el agujero tipico: un caso que nadie previo y el flujo
                  se queda sin camino. */}
              {!salidas.some(x => x.clave === "resto") && (
                <button className="btn"
                  onClick={() => onCambiarPaso?.(id, {
                    salidas: [...salidas, { clave: "resto", nombre: "Resto" }],
                  })}
                  title="Una salida por defecto para lo que no encaje en ninguna otra"
                  style={{ padding: "4px 9px", borderRadius: 6, fontSize: 11,
                    cursor: "pointer", border: "1px dashed var(--border)",
                    background: "var(--surface)", color: "var(--text3)",
                    fontFamily: "var(--font)" }}>
                  Añadir «Resto»
                </button>
              )}
            </div>
          )}
        </Bloque>
      )}

      {kind === "condicional" && (
        <div className="cw-field" style={{ marginBottom: 11 }}>
          <Campo><label>La pregunta de este nodo</label>
          <input defaultValue={t.condicion || ""} key={id + "-cond"} disabled={!puede("condicion")}
            onChange={e => cambiarTexto(id, { condicion: e.target.value })}
            placeholder="¿Lo aprueba el cliente?" /></Campo>
        </div>
      )}

      {(t.status === "Bloqueada" || t.nota_bloqueo) && (
        <div className="cw-field" style={{ marginBottom: 11 }}>
          <Campo><label>Por que esta bloqueado</label>
          <input defaultValue={t.nota_bloqueo || ""} key={id + "-nb"} disabled={!puede("nota_bloqueo")}
            onChange={e => cambiarTexto(id, { nota_bloqueo: e.target.value })}
            placeholder="Que hace falta para desbloquearlo" /></Campo>
        </div>
      )}

      {/* Tiempo real. Un guion y nunca un cero: un cero se leeria como "no se ha
          trabajado" en vez de "no hay dato". */}
      {esTrabajo && (
        <Bloque titulo="Tiempo">
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--text2)" }}>
            <div>
              <div style={{ fontSize: 9, color: "var(--text3)", fontWeight: 700 }}>REAL</div>
              <div style={{ fontFamily: "var(--mono)" }}>{real ? fmtHours(real / 60) : "—"}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "var(--text3)", fontWeight: 700 }}>ESTIMADO</div>
              <div style={{ fontFamily: "var(--mono)" }}>{est ? fmtHours(est / 60) : "—"}</div>
            </div>
            {!!est && !!real && (
              <div>
                <div style={{ fontSize: 9, color: "var(--text3)", fontWeight: 700 }}>DESVIO</div>
                <div style={{ fontFamily: "var(--mono)",
                  color: real > est ? "var(--red-text)" : "var(--ok-text)" }}>
                  {real > est ? "+" : ""}{Math.round((real / est - 1) * 100)}%
                </div>
              </div>
            )}
          </div>
          {!est && (
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 6, lineHeight: 1.5 }}>
              Sin estimacion no se puede contrastar lo previsto con lo trabajado; el avance
              del paso se calcula solo por su estado.
            </div>
          )}
        </Bloque>
      )}

      {esTrabajo && !!(t.asignados || []).length && (
        <Bloque titulo="Quien lo lleva">
          {PJ_ROLES.map(r => {
            const gente = (t.asignados || []).filter(x => x.rol === r.key);
            if (!gente.length) return null;
            return (
              <div key={r.key} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "var(--text3)",
                  textTransform: "uppercase", letterSpacing: ".06em" }} title={r.sub}>
                  {r.label}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
                  {gente.map(x => (
                    <div key={x.user_id} style={{ display: "flex", alignItems: "center", gap: 5,
                      fontSize: 11.5, color: "var(--text2)" }}>
                      <Avatar name={x.name} size={18}
                        color={r.key === "encargado" ? "var(--accent)" : "var(--text3)"} />
                      {x.name}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </Bloque>
      )}

      <Bloque titulo={`Conexiones (${cx.llegan.length + cx.salen.length})`}>
        {!cx.llegan.length && !cx.salen.length && (
          <div style={{ fontSize: 11, color: "var(--text3)" }}>
            Este paso todavia no esta conectado a nada.
          </div>
        )}
        {cx.llegan.map(e => (
          <FilaConexion key={"i" + e.id} e={e} otro={nodos[String(e.from_task_id)]}
            entra onSeleccionar={onSeleccionar} />
        ))}
        {cx.salen.map(e => (
          <FilaConexion key={"o" + e.id} e={e} otro={nodos[String(e.to_task_id)]}
            onSeleccionar={onSeleccionar} />
        ))}
      </Bloque>

      {!editableTarea && (
        <div style={{ fontSize: 10.5, color: "var(--text3)", background: "var(--surface2)",
          padding: "7px 10px", borderRadius: 8, marginTop: 12, lineHeight: 1.5 }}>
          Este paso no esta asignado a ti y no llevas el proyecto, asi que solo lo ves.
        </div>
      )}

      {proyecto?.can_edit && (
        <div style={{ marginTop: 16, paddingTop: 13, borderTop: "1px solid var(--border2)" }}>
          <button className="btn" onClick={() => onBorrarPaso?.(t)}
            style={{ width: "100%", padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              fontSize: 12, fontFamily: "var(--font)", background: "var(--red-bg)",
              border: "1px solid #fca5a5", color: "var(--red-text)", fontWeight: 600 }}>
            Eliminar el paso
          </button>
          {!!(cx.llegan.length + cx.salen.length) && (
            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 5, textAlign: "center" }}>
              se llevara {cx.llegan.length + cx.salen.length} conexion(es)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Cabecera = ({ titulo, icono, color, onCerrar }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 13 }}>
    {icono && <Icon name={icono} size={13} color={color || "var(--text3)"} />}
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".08em", color: "var(--text3)", flex: 1 }}>{titulo}</span>
    <button aria-label="Cerrar" className="btn" onClick={onCerrar} title="Cerrar"
      style={{ background: "none", border: "none", cursor: "pointer", padding: 3 }}>
      <Icon name="x-lg" size={11} color="var(--text3)" />
    </button>
  </div>
);

const Bloque = ({ titulo, children }) => (
  <div style={{ marginTop: 13, paddingTop: 11, borderTop: "1px solid var(--border2)" }}>
    <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".07em", color: "var(--text3)", marginBottom: 7 }}>{titulo}</div>
    {children}
  </div>
);

const FilaConexion = ({ e, otro, entra, onSeleccionar }) => (
  <div className="btn" {...clicable(() => onSeleccionar?.(e))}
    style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 7px",
      borderRadius: 7, cursor: "pointer", fontSize: 11.5, color: "var(--text2)",
      background: "var(--surface2)", marginBottom: 4 }}>
    <Icon name={entra ? "arrow-left" : "chevron-right"} size={10} color="var(--text3)" />
    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
      whiteSpace: "nowrap" }}>{otro?.title || "?"}</span>
    {e.etiqueta && <Badge label={e.etiqueta} color="var(--text2)" bg="var(--surface)" />}
    {e.opcional && <Icon name="dash-lg" size={9} color="#8b5cf6" />}
  </div>
);
